import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { DocumentCategory, Visibility, type DocumentSummary, type Passage } from '@syndiqia/documents/contracts';
import { useAuth } from 'react-oidc-context';
import { errorMessage, useApi } from '../api';
import { devSubject } from '../config';
import { AskQuestion } from '../components/AskQuestion';
import { Alert, Disclosure, Empty, Field, Loading, PageHeader } from '../components/ui';
import { useI18n } from '../i18n';
import { useRegistryMutation } from '../queries';
import { useSyndicate } from '../session';

function useDocuments() {
  const api = useApi();
  const s = useSyndicate();
  return useQuery({ queryKey: [s.tenantId, 'documents'], queryFn: () => api<DocumentSummary[]>(`${s.api}/documents`) });
}

/** Files are fetched with the session token, then opened from memory: no shareable link ever exists. */
export function useOpenDocument() {
  const auth = useAuth();
  const s = useSyndicate();
  return async (doc: Pick<DocumentSummary, 'id'>) => {
    const tab = window.open('', '_blank');
    const res = await fetch(`${s.api}/documents/${doc.id}/file`, {
      headers: { ...(auth.user?.access_token ? { Authorization: `Bearer ${auth.user.access_token}` } : {}), ...(devSubject ? { 'x-dev-subject': devSubject } : {}) },
    });
    if (!res.ok) return tab?.close();
    const url = URL.createObjectURL(await res.blob());
    if (tab) tab.location.href = url;
    else window.location.href = url;
  };
}

export function DocumentsPage() {
  const { t, date } = useI18n();
  const s = useSyndicate();
  const documents = useDocuments();
  const open = useOpenDocument();
  const remove = useRegistryMutation<string, void>((api, base, id) => api(`${base.replace(/\/registry$/, '/documents')}/${id}`, { method: 'DELETE' }), ['documents']);
  const groups = DocumentCategory.options
    .map((c) => ({ category: c, docs: documents.data?.filter((d) => d.category === c) ?? [] }))
    .filter((g) => g.docs.length > 0);

  return (
    <>
      <PageHeader title={t.documents.title} actions={s.can('documents.manage') && <Disclosure label={t.documents.upload}>{(close) => <UploadForm onDone={close} />}</Disclosure>} />
      {s.can('agents.ask') && <AskQuestion onOpenDocument={(id) => void open({ id })} />}
      <DocumentSearch />
      {documents.isPending && <Loading label={t.loading} />}
      {documents.data?.length === 0 && <Empty>{t.documents.empty}</Empty>}
      {groups.map((g) => (
        <section key={g.category}>
          <h2>{t.documents.categories[g.category]}</h2>
          <ul className="list-cards">
            {g.docs.map((d) => (
              <li key={d.id} className="panel doc-row">
                <div>
                  <button type="button" className="link-button doc-title" onClick={() => void open(d)}>
                    {d.title}
                  </button>
                  <div className="muted small">
                    {d.documentDate ? date(d.documentDate) : ''} · {t.documents.pages(d.pageCount)}
                    {d.visibility === 'board' && <span className="badge badge-draft"> {t.documents.boardOnly}</span>}
                  </div>
                </div>
                {s.can('documents.manage') && (
                  <button type="button" className="link-button small" onClick={() => window.confirm(t.documents.confirmDelete(d.title)) && remove.mutate(d.id)}>
                    {t.documents.delete}
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

function DocumentSearch() {
  const { t } = useI18n();
  const api = useApi();
  const s = useSyndicate();
  const open = useOpenDocument();
  const [q, setQ] = useState('');
  const [submitted, setSubmitted] = useState('');
  const results = useQuery({
    queryKey: [s.tenantId, 'documents', 'search', submitted],
    queryFn: () => api<Passage[]>(`${s.api}/documents/search?q=${encodeURIComponent(submitted)}`),
    enabled: submitted.length >= 2,
  });
  return (
    <section className="doc-search">
      <form onSubmit={(e) => (e.preventDefault(), setSubmitted(q.trim()))}>
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.documents.search} />
      </form>
      {results.data && (
        <div className="panel">
          {results.data.length === 0 && <p className="muted">{t.documents.noResults}</p>}
          <ul className="list-cards">
            {results.data.map((p) => (
              <li key={`${p.documentId}-${p.pageNo}`}>
                <button type="button" className="link-button" onClick={() => void open({ id: p.documentId })}>
                  {p.title}, {t.documents.page(p.pageNo)}
                </button>
                <p className="muted small excerpt">{p.excerpt}</p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
}

function UploadForm({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const auth = useAuth();
  const s = useSyndicate();
  const [values, setValues] = useState({ title: '', category: 'minutes', visibility: 'members', documentDate: '' });
  const [file, setFile] = useState<File | null>(null);
  const upload = useRegistryMutation<FormData, DocumentSummary>(
    async (_api, base, form) => {
      const res = await fetch(base.replace(/\/registry$/, '/documents'), {
        method: 'POST',
        headers: { ...(auth.user?.access_token ? { Authorization: `Bearer ${auth.user.access_token}` } : {}), ...(devSubject ? { 'x-dev-subject': devSubject } : {}) },
        body: form,
      });
      const body = await res.json();
      if (!res.ok) throw Object.assign(new Error(body.message ?? 'Upload failed'), { status: res.status, body });
      return body as DocumentSummary;
    },
    ['documents'],
  );
  const set = (key: keyof typeof values) => (e: { target: { value: string } }) => setValues((v) => ({ ...v, [key]: e.target.value }));
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!file || !values.title.trim()) return;
    // Fields first, file last: the server reads the fields that precede the file.
    const form = new FormData();
    form.append('title', values.title.trim());
    form.append('category', values.category);
    form.append('visibility', values.visibility);
    if (values.documentDate) form.append('documentDate', values.documentDate);
    form.append('file', file);
    upload.mutate(form, { onSuccess: onDone });
  };
  return (
    <form onSubmit={submit} className="form" noValidate>
      <Field label={t.documents.file}>
        <input
          type="file"
          accept=".pdf,.txt,.md,application/pdf,text/plain"
          onChange={(e) => {
            const f = e.target.files?.[0] ?? null;
            setFile(f);
            if (f && !values.title) setValues((v) => ({ ...v, title: f.name.replace(/\.[^.]+$/, '').replace(/[-_]+/g, ' ') }));
          }}
        />
      </Field>
      <Field label={t.documents.docTitle}>
        <input value={values.title} onChange={set('title')} />
      </Field>
      <div className="row">
        <Field label={t.documents.category}>
          <select value={values.category} onChange={set('category')}>
            {DocumentCategory.options.map((c) => (
              <option key={c} value={c}>
                {t.documents.categories[c]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.documents.visibility}>
          <select value={values.visibility} onChange={set('visibility')}>
            {Visibility.options.map((v) => (
              <option key={v} value={v}>
                {t.documents.visibilities[v]}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t.documents.date} hint={t.common.optional}>
          <input type="date" value={values.documentDate} onChange={set('documentDate')} />
        </Field>
      </div>
      {upload.isError && <Alert>{errorMessage(upload.error, t)}</Alert>}
      <div className="form-actions">
        <button type="submit" className="button button-primary" disabled={!file || upload.isPending}>
          {t.common.save}
        </button>
        <button type="button" className="button" onClick={onDone}>
          {t.common.cancel}
        </button>
      </div>
    </form>
  );
}
